import { useState } from 'react'
import { isRole, roleHasCapability } from '@qim/shared'
import { fetchFleetCost } from '../api.js'
import { formatMoney } from '../format.js'
import { QrveyBadge } from './account-settings.jsx'
import type { FleetCost as FleetCostData } from '../api.js'
import type { JSX } from 'react'

/**
 * What the whole estate is spending (IM-130).
 *
 * ## Why this is a button on the fleet rather than a column in it
 *
 * The fleet is the console's front door and loads on every visit. A cost column
 * would mean one `GetCostAndUsage` per account per page load, each charging that
 * **customer's** account about a cent — so opening the console would bill every
 * customer, and opening it often would bill them often. There is no version of
 * that which is acceptable, so cost is asked for and never rendered by default.
 *
 * ## Why accounts that did not answer are named
 *
 * A total that quietly omits three accounts is worse than one that says which
 * three: the number looks complete and is not, and somebody will compare it with
 * a bill and conclude the bill is wrong. This is the same rule the fleet already
 * follows for a customer whose records would not read.
 *
 * ## No verdict, here either
 *
 * A dollar figure beside an age makes the temptation stronger rather than
 * weaker, and the standing rule does not bend for it. This reports what is
 * spent. Nothing is called waste, nothing is ranked as a candidate for anything,
 * and there is no destroy link.
 */

const canRead = (role: string): boolean => isRole(role) && roleHasCapability(role, 'cost:read')

export const FleetCost = ({
  baseUrl,
  role,
}: {
  readonly baseUrl: string
  readonly role: string
}): JSX.Element | null => {
  const [cost, setCost] = useState<FleetCostData | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  if (!canRead(role)) return null

  const read = async (): Promise<void> => {
    setBusy(true)
    setProblem(undefined)
    try {
      setCost(await fetchFleetCost(baseUrl))
    } catch {
      setProblem('Could not read costs just now.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="panel">
      <h3>Cost</h3>

      <button
        type="button"
        className="ghost"
        disabled={busy}
        onClick={() => {
          void read()
        }}
      >
        {cost === undefined ? 'Read the estate’s cost' : 'Read again'}
      </button>
      <p className="hint">
        One AWS Cost Explorer request per account, each charging that customer&rsquo;s account about
        a cent &mdash; so it happens when you ask rather than when this page loads. The data is
        about a day behind.
      </p>

      {busy ? <p role="status">Reading…</p> : null}

      {cost === undefined ? null : (
        <>
          <p>
            <strong>{formatMoney(cost.total, cost.currency)}</strong>{' '}
            <span className="muted">
              across {cost.accounts.length} account{cost.accounts.length === 1 ? '' : 's'},{' '}
              {cost.period.describes ?? `${cost.period.from} to ${cost.period.to}`}
            </span>
          </p>

          {cost.accounts.length === 0 ? (
            <p role="status">No account answered with a cost figure.</p>
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Account</th>
                    <th scope="col">Customer</th>
                    <th scope="col">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {[...cost.accounts]
                    .sort((a, b) => b.amount - a.amount)
                    .map((row) => (
                      <tr key={`${row.tenantId}/${row.accountKey}`}>
                        <td className="mono-cell">
                          {row.accountKey}
                          <p className="muted">{row.label}</p>
                        </td>
                        <td>
                          {row.tenantName} <QrveyBadge kind={row.tenantKind} />
                        </td>
                        <td>{formatMoney(row.amount, row.currency)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Named rather than dropped: a total that quietly omits accounts
            looks complete and is not. */}
          {cost.unreadable.length === 0 ? null : (
            <p className="hint">
              Not included, because their cost could not be read:{' '}
              {cost.unreadable.map((gap) => gap.accountKey).join(', ')}. Most often an access stack
              that predates the permission &mdash; their onboarding screen offers the template.
            </p>
          )}
        </>
      )}

      {problem === undefined ? null : (
        <p role="alert" className="failure">
          {problem}
        </p>
      )}
    </section>
  )
}
